import React, { ReactNode, RefObject, useEffect, useRef } from 'react';

import KEYS from '@rover/constants/src/platform/keys.constants';
import { Box } from '@rover/kibble/core';

type FilterDropdownProps = {
  children: ReactNode;
  onClose: () => void;
  triggerRef?: RefObject<HTMLElement | null>;
  align?: 'left' | 'right';
  width?: string;
  id?: string;
  role?: 'listbox' | 'dialog' | 'region';
  'aria-label'?: string;
  'aria-labelledby'?: string;
  'aria-activedescendant'?: string;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
};

const FilterDropdown = React.forwardRef<HTMLDivElement, FilterDropdownProps>(
  (
    {
      children,
      onClose,
      triggerRef,
      align = 'left',
      width = '100%',
      id,
      role,
      'aria-label': ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-activedescendant': ariaActivedescendant,
      onKeyDown,
    },
    ref
  ): JSX.Element => {
    const internalRef = useRef<HTMLDivElement>(null);
    const dropdownRef = (ref as React.RefObject<HTMLDivElement>) || internalRef;

    // Close dropdown when clicking outside both the dropdown and trigger
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent): void => {
        const target = event.target as Node;
        const clickedOutsideDropdown = dropdownRef.current && !dropdownRef.current.contains(target);
        const clickedOutsideTrigger = triggerRef?.current && !triggerRef.current.contains(target);

        // Only close if clicked outside both the dropdown and trigger
        if (clickedOutsideDropdown && (!triggerRef || clickedOutsideTrigger)) {
          onClose();
        }
      };

      // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
      document.addEventListener('mousedown', handleClickOutside);

      return () => {
        // eslint-disable-next-line rover/no-platform-specific-globals-or-imports
        document.removeEventListener('mousedown', handleClickOutside);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onClose, triggerRef]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === KEYS.ESCAPE) {
        event.preventDefault();
        onClose();
      }
      onKeyDown?.(event);
    };

    return (
      <Box
        ref={dropdownRef}
        id={id}
        webProps={{
          role,
          'aria-label': ariaLabel,
          'aria-labelledby': ariaLabelledby,
          'aria-activedescendant': ariaActivedescendant,
          tabIndex: -1,
          onKeyDown: handleKeyDown,
        }}
        sx={(theme) => ({
          position: 'absolute',
          top: 'calc(100% + 3px)',
          left: align === 'left' ? 0 : undefined,
          right: align === 'right' ? 0 : undefined,
          borderRadius: theme.radii.primary,
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
          border: '1px solid',
          borderColor: 'border.secondary',
          borderTop: 'none',
          zIndex: 1000,
          width,
          // The panel takes focus when it opens, so keyboard users need a ring on
          // it; :focus-visible keeps the ring off mouse-opened dropdowns.
          outline: 'none',
          '&:focus-visible': {
            outline: '5px auto -webkit-focus-ring-color',
            outlineOffset: '2px',
          },
        })}
      >
        {children}
      </Box>
    );
  }
);

export default FilterDropdown;
